import { CACHED_AT_HEADER } from "../utils/api-cache";

export const noop = () => {};

export const UNREACHABLE_TEXT =
  "Der Server ist gerade nicht erreichbar. Bitte versuche es gleich noch einmal.";

const RETRY_TEXT = "Bitte versuche es noch einmal.";

export class ApiError extends Error {
  constructor(response, body) {
    super(`HTTP ${response.status}`);
    this.name = "ApiError";
    this.status = response.status;
    this.body = body;
  }
}

const cachedResults = new WeakSet();

export const isCachedResult = (result) =>
  result instanceof Object && cachedResults.has(result);

export const apiFetch = async (
  sessionId,
  path,
  { method = "GET", body, idempotencyKey, keepalive = false } = {},
) => {
  const headers = { "Session-ID": sessionId };
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  const options = { method, headers };
  if (keepalive) options.keepalive = true;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}${path}`,
    options,
  );
  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = undefined;
    }
    throw new ApiError(response, errorBody);
  }
  const result = await response.json();
  if (response.headers?.has(CACHED_AT_HEADER) && result instanceof Object) {
    cachedResults.add(result);
  }
  return result;
};

const isClientError = (error) =>
  error instanceof ApiError && error.status >= 400 && error.status < 500;

export const isUnreachable = (error) =>
  !(error instanceof ApiError) || error.status === 503;

const RETRYABLE_CLIENT_ERRORS = new Set([408, 429]);

export const isRetryable = (error) =>
  !(error instanceof ApiError) ||
  error.status >= 500 ||
  RETRYABLE_CLIENT_ERRORS.has(error.status) ||
  error.body?.code === "IDEMPOTENCY_KEY_IN_FLIGHT";

export const errorMessage = (error, fallback) => {
  if (isClientError(error)) {
    return error.body?.message || fallback;
  }
  if (error instanceof ApiError && error.status !== 503) {
    console.error(error);
    return `${fallback} ${RETRY_TEXT}`;
  }
  if (!(error instanceof ApiError)) {
    console.error(error);
  }
  return UNREACHABLE_TEXT;
};

export const reportTo = (onError, fallback) => (error) =>
  onError(errorMessage(error, fallback));

export const run = (request, onSuccess, onError) =>
  request.then(onSuccess, onError).catch((error) => console.error(error));
